import type { PaymentMethod } from '@massilia/prisma-generated'

import { useAppForm } from '@massilia/features'
import {
  type BookingSessionResponse,
  countBillablePassengers,
  setAccessToken,
  toError,
  useCheckoutRegisterMutation,
  useInitiateSessionCheckout,
  useSubmitDepositReservation,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { differenceInMonths } from 'date-fns'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { BookingStepActions } from '@/components/booking/booking-step-actions'
import { PromoCodeSlot } from '@/components/booking/promo-code-slot'
import { TripHeader } from '@/components/booking/trip-header'
import { usePromoCode } from '@/components/booking/use-promo-code'
import { useAuthStore } from '@/stores/auth.store'

import { type CheckoutErrorType, toCheckoutError } from './-sections/checkout-error'
import { CheckoutErrorCard } from './-sections/checkout-error-card'
import { ContactInfoSection } from './-sections/contact-info-section'
import { PaymentMethodSelector } from './-sections/payment-method-selector'
import { PaymentSection } from './-sections/payment-section'
import { PaymentTimingSection } from './-sections/payment-timing-section'
import { StepCard } from './-sections/step-card'
import {
  type CheckoutFormInput,
  checkoutFormSchema,
  type PaymentMethodFormValue,
  type paymentMethods,
  type PaymentTimingFormValue,
} from './checkout-form.schema'

interface CheckoutComponentProps {
  prefillEmail?: string
  sessionId: string
  isLoading: boolean
  prefillPhone?: string
  session?: BookingSessionResponse
}

export function CheckoutComponent({
  prefillEmail,
  sessionId,
  isLoading,
  prefillPhone,
  session,
}: CheckoutComponentProps) {
  const navigate = useNavigate()
  const updateAuth = useAuthStore((state) => state.updateAuth)

  // Échec de lancement du paiement. Affiché en ligne : sans ce retour, une panne
  // du prestataire laissait l'écran inchangé et le client recliquait sur
  // « Payer » — d'où des réservations et des mails en double.
  const [checkoutError, setCheckoutError] = useState<null | {
    message?: string
    type: CheckoutErrorType
  }>(null)

  // Promo code: the discount itself lives on the session (server is source of truth).
  const {
    isVerifying: isPromoCodePending,
    promoCodeError,
    remove: handleRemovePromoCode,
    verify: handleVerifyPromoCode,
  } = usePromoCode(sessionId)

  const { isPending: isCheckoutPending, mutateAsync: initiateCheckout } =
    useInitiateSessionCheckout(sessionId)
  const { isPending: isDepositPending, mutateAsync: submitDepositReservation } =
    useSubmitDepositReservation(sessionId)
  const { mutateAsync: checkoutRegister } = useCheckoutRegisterMutation()

  const promoCodeDiscount = session?.promoCode ? session.promoCode.discountCents / 100 : null
  const billablePassengerCount = countBillablePassengers(session?.flightDetails.passengersCount)

  // Données dérivées — all from session (server is source of truth)
  const flightPriceCents = session?.totalFlightPriceCents ?? 0
  const insurancePriceCents = session?.insurance?.insurancePriceCents ?? 0
  const flightPrice = flightPriceCents / 100
  const insurancePrice = insurancePriceCents / 100
  const totalPrice = flightPrice + insurancePrice
  const paymentDeadline = session?.paymentDeadline
  const depositDeadline = session?.depositDeadline
  const isReunion = session?.departureAirportCode === 'RUN'

  const installmentAvailable = session?.installmentInfo?.available ?? false
  // Prestataire installments piloté par le flag runtime serveur. Fallback aligné
  // sur le défaut serveur (FLOA) : il pilote le libellé ET la redirection de fin
  // de paiement, un défaut SSP ici ouvrait un onglet vide sur un checkout FLOA.
  const installmentProvider = session?.installmentInfo?.provider ?? 'FLOA'
  const minimumDeposit = installmentAvailable
    ? (session?.installmentInfo?.minimumDepositCents ?? 0) / 100
    : 0

  const isInstallmentsDisabled =
    installmentAvailable &&
    !!session?.flightDetails?.departureDate &&
    differenceInMonths(session.flightDetails.departureDate, new Date()) > 4

  const form = useAppForm({
    onSubmitInvalid: () => {
      const invalidInput = document.querySelector('[aria-invalid="true"]') as HTMLInputElement
      invalidInput?.scrollIntoView({ behavior: 'smooth', inline: 'center' })
      invalidInput?.focus()
    },
    defaultValues: {
      buyerEmail: prefillEmail ?? '',
      billingCity: '',
      billingCountryCode: { label: '', value: '' },
      billingStreet: '',
      billingZipCode: '',
      buyerPhone: prefillPhone ?? '',
      createAccount: false,
      depositAmount: minimumDeposit,
      floaPayType: '3XG',
      isPayingInMultipleTimes: false,
      password: '',
      passwordConfirmation: '',
      paymentMethod: 'credit-card' as (typeof paymentMethods)[number],
      // Aucun délai présélectionné : l'utilisateur doit choisir explicitement.
      paymentTiming: undefined as PaymentTimingFormValue | undefined,
      subscribeToNotifications: false,
    } satisfies CheckoutFormInput,
    onSubmit: async ({ value }) => {
      const tryCreateAccount = async (email: string) => {
        if (!value.createAccount || !value.password || !value.passwordConfirmation) return

        try {
          const user = await checkoutRegister({
            email: email.trim(),
            password: value.password,
            passwordConfirmation: value.passwordConfirmation,
          })

          setAccessToken(user.accessToken)
          updateAuth({
            isAuthenticated: true,
            user: {
              email: user.email,
              firstName: user.firstName,
              id: user.id,
              lastName: user.lastName,
            },
          })
        } catch {
          // Account creation failed (e.g., account already exists).
          // Do not block the checkout — the booking is already created.
        }
      }

      // "Pay later" flow: skip Axepta, record the reservation request, redirect to pending.
      if (value.paymentTiming === 'PAY_LATER') {
        const result = await submitDepositReservation({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        const bookingReference = result.checkout?.bookingReference ?? ''
        void navigate({
          params: { status: 'pending' },
          search: { bookingRef: bookingReference, session: sessionId },
          to: '/confirmation-paiement/$status',
        })
        return
      }

      const shouldPayDeposit = installmentAvailable && value.isPayingInMultipleTimes

      if (value.paymentMethod === 'credit-card' || value.paymentMethod === 'installments') {
        // Redirect payment flow (credit card via Axepta, installments via SSP ou FLOA selon le flag)
        const isInstallments = value.paymentMethod === 'installments'
        const isSspInstallments = isInstallments && installmentProvider === 'SSP'
        const isFloaInstallments = isInstallments && installmentProvider === 'FLOA'

        // SSP ne redirige pas l'utilisateur après paiement : on ouvre sa page dans
        // un nouvel onglet. On l'ouvre de façon SYNCHRONE ici, dans le geste
        // utilisateur — l'ouvrir après l'`await` ci-dessous ferait bloquer le popup
        // par le navigateur (plus rattaché au clic). On met `opener = null` pour
        // reproduire l'isolation de `noopener` tout en gardant la référence
        // nécessaire pour y injecter l'URL une fois le lien SSP obtenu.
        // FLOA (comme la CB) redirige l'utilisateur en fin de paiement : même onglet.
        const paymentWindow = isSspInstallments ? window.open('about:blank', '_blank') : null
        if (paymentWindow) paymentWindow.opener = null

        try {
          const result = await initiateCheckout({
            buyerEmail: value.buyerEmail.trim(),
            buyerPhone: value.buyerPhone.trim(),
            depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
            paymentMethod: isInstallments ? 'INSTALLMENTS' : 'CREDIT_CARD',
            paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
            subscribeToNotifications: value.subscribeToNotifications,
            ...(isInstallments && {
              billingAddress: {
                city: value.billingCity,
                countryCode: value.billingCountryCode.value,
                street: value.billingStreet,
                zipCode: value.billingZipCode,
              },
            }),
            ...(isFloaInstallments && { floaPayType: value.floaPayType ?? '3XG' }),
          })

          await tryCreateAccount(value.buyerEmail)

          const checkout = result.checkout
          if (!checkout?.paymentRedirectUrl) {
            throw new Error('No checkout redirect URL returned from server')
          }

          if (isSspInstallments) {
            // Injecte l'URL dans l'onglet déjà ouvert puis bascule l'onglet
            // d'origine sur la page de confirmation en attente. Si le popup a tout
            // de même été bloqué (paymentWindow null), cette page propose le lien
            // de paiement via le search param `paymentRedirectUrl`.
            if (paymentWindow) paymentWindow.location.href = checkout.paymentRedirectUrl
            void navigate({
              params: { status: 'pending' },
              search: {
                bookingRef: checkout.bookingReference,
                paymentRedirectUrl: checkout.paymentRedirectUrl,
                session: sessionId,
              },
              to: '/confirmation-paiement/$status',
            })
          } else {
            // CB Axepta et FLOA : redirection même onglet, retour via URLSuccess/URLFailure
            window.location.assign(checkout.paymentRedirectUrl)
          }
        } catch (error) {
          // Referme l'onglet de paiement resté vide si le checkout a échoué.
          paymentWindow?.close()
          throw error
        }
      } else {
        const paymentMethodMap = {
          'bank-transfer': 'BANK_TRANSFER',
          'cash': 'CASH',
          'vacation-cheque': 'VACATION_CHEQUE',
          'vacation-e-cheque': 'VACATION_E_CHEQUE',
        } satisfies Record<string, PaymentMethod>

        const result = await initiateCheckout({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
          paymentMethod: paymentMethodMap[value.paymentMethod],
          paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        // Non-CC flow: navigate client-side to the pending confirmation page
        const bookingReference = result.checkout?.bookingReference ?? ''
        void navigate({
          params: { status: 'pending' },
          search: { bookingRef: bookingReference, session: sessionId },
          to: '/confirmation-paiement/$status',
        })
      }
    },
    validationLogic: revalidateLogic({ mode: 'submit', modeAfterSubmission: 'blur' }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, checkoutFormSchema),
    },
  })

  const handleBack = () => {
    void navigate({
      search: { session: sessionId },
      to: '/assurances',
    })
  }

  if (isLoading && !session) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Loader2 className="size-8 animate-spin text-brand-600" />
      </div>
    )
  }

  const sidebar = (
    <BookingSidebar
      promoCodeDiscount={promoCodeDiscount}
      promoCodeSlot=<PromoCodeSlot
        appliedCode={session?.promoCode?.code ?? null}
        discountEuros={promoCodeDiscount}
        error={promoCodeError}
        isVerifying={isPromoCodePending}
        onRemove={() => {
          void handleRemovePromoCode()
        }}
        onVerify={(code) => {
          void handleVerifyPromoCode(code)
        }}
        passengerCount={billablePassengerCount}
        scope={session?.promoCode?.scope}
      />
      session={session}
    />
  )

  return (
    <>
      <form
        className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setCheckoutError(null)
          // handleSubmit re-jette l'erreur d'onSubmit : sans ce catch elle
          // finissait en unhandled rejection, écran muet côté client.
          form.handleSubmit().catch((error) => {
            setCheckoutError(toCheckoutError(toError(error)))
          })
        }}
      >
        {/* Colonne droite — Sidebar (desktop) */}
        <div className="hidden w-full shrink-0 lg:order-last lg:block lg:w-96">{sidebar}</div>

        {/* Colonne gauche — Formulaire */}
        <div className="flex flex-1 flex-col gap-9">
          {/* Récap voyage en tête, mobile comme desktop (sans frise à cette étape) */}
          {session && <TripHeader session={session} showTimeline={false} />}

          <div className="flex w-full flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-light-900">
              Résumé des informations et paiement
            </h2>
            <div className="w-full text-sm text-gray-light-900">
              Vous avez sélectionné les services suivants pour votre voyage. Veuillez vérifier les
              informations avant de finaliser votre réservation.
            </div>
          </div>

          <div className="flex w-full flex-col gap-6">
            {/* Informations de contact + options compte */}
            <ContactInfoSection
              fields={{
                buyerEmail: 'buyerEmail',
                buyerPhone: 'buyerPhone',
                createAccount: 'createAccount',
                password: 'password',
                passwordConfirmation: 'passwordConfirmation',
                paymentMethod: 'paymentMethod',
                subscribeToNotifications: 'subscribeToNotifications',
              }}
              form={form}
            />

            <form.Subscribe selector={(state) => state.values.paymentTiming}>
              {(paymentTiming) => (
                <>
                  {/* Délai de paiement — maintenant ou avant la date d'acompte */}
                  <StepCard
                    step={1}
                    subtitle="Choisissez quand vous souhaitez régler votre voyage"
                    title="Délai de paiement"
                  >
                    <PaymentTimingSection
                      depositDeadline={depositDeadline}
                      fields={{ paymentTiming: 'paymentTiming' }}
                      form={form}
                      payNowContent={
                        installmentAvailable ? (
                          <PaymentSection
                            fields={{
                              depositAmount: 'depositAmount',
                              isPayingInMultipleTimes: 'isPayingInMultipleTimes',
                            }}
                            form={form}
                            installmentAvailable={installmentAvailable}
                            minimumDeposit={minimumDeposit}
                            promoCodeDiscount={promoCodeDiscount}
                            totalPrice={totalPrice}
                          />
                        ) : undefined
                      }
                    />
                  </StepCard>

                  {paymentTiming === 'PAY_NOW' && (
                    <>
                      {/* Mode de paiement — CB, virement, etc. */}
                      <StepCard
                        step={2}
                        subtitle="Tous les paiements sont sécurisés avec nos prestataires"
                        title="Mode de paiement"
                      >
                        <PaymentMethodSelector
                          fields={{
                            billingCity: 'billingCity',
                            billingCountryCode: 'billingCountryCode',
                            billingStreet: 'billingStreet',
                            billingZipCode: 'billingZipCode',
                            floaPayType: 'floaPayType',
                            paymentMethod: 'paymentMethod',
                          }}
                          form={form}
                          installmentProvider={installmentProvider}
                          paymentDeadline={paymentDeadline}
                          isInstallmentsDisabled={isInstallmentsDisabled}
                          isReunion={isReunion}
                          totalPrice={totalPrice}
                        />
                      </StepCard>
                    </>
                  )}
                </>
              )}
            </form.Subscribe>

            {checkoutError && (
              <CheckoutErrorCard message={checkoutError.message} type={checkoutError.type} />
            )}

            {/* Boutons de navigation */}
            <BookingStepActions className="md:justify-end">
              <Button className="gap-2" onClick={handleBack} type="button" variant="ghost">
                <ChevronLeft className="h-4 w-4 text-brand-700" />
                <div className="text-sm font-medium text-brand-700">Étape précédente</div>
              </Button>
              <form.Subscribe
                selector={(state) => ({
                  isSubmitting: state.isSubmitting,
                  paymentMethod: state.values.paymentMethod,
                  paymentTiming: state.values.paymentTiming,
                })}
              >
                {({ isSubmitting, paymentMethod, paymentTiming }) => {
                  const submitLabel = getSubmitLabel(paymentTiming, paymentMethod)
                  return (
                    <Button
                      className="min-w-44 gap-2 px-6"
                      disabled={isSubmitting || isCheckoutPending || isDepositPending}
                      type="submit"
                    >
                      {isSubmitting || isCheckoutPending || isDepositPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      ) : (
                        <div className="text-sm font-medium text-white">{submitLabel}</div>
                      )}
                    </Button>
                  )
                }}
              </form.Subscribe>
            </BookingStepActions>
          </div>
        </div>
      </form>
    </>
  )
}

// Libellé du bouton de soumission : dépend du mode de paiement choisi (immédiat
// vs demande à traiter plus tard) et, si paiement immédiat, du moyen retenu.
function getSubmitLabel(
  paymentTiming: PaymentTimingFormValue | undefined,
  paymentMethod: PaymentMethodFormValue
): string {
  if (!paymentTiming) return 'Payer'
  if (paymentTiming === 'PAY_LATER') return 'Confirmer ma demande'
  if (paymentMethod === 'credit-card' || paymentMethod === 'installments') return 'Payer'
  return 'Réserver'
}
